import React from "react";
import { Car, CloudRain, Waves, TrafficCone, Ban, CircleAlert, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { reportIncident } from "../../lib/api";

const TYPES = [
  { key: "Stalled Vehicle", Icon: Car, sev: "medium", tint: "#FFC726" },
  { key: "Flooding", Icon: Waves, sev: "high", tint: "#FF5C7A" },
  { key: "Heavy Rain", Icon: CloudRain, sev: "medium", tint: "#5B9BFF" },
  { key: "Crash", Icon: CircleAlert, sev: "high", tint: "#FF5C7A" },
  { key: "Debris on Road", Icon: TrafficCone, sev: "medium", tint: "#FFC726" },
  { key: "Road Blocked", Icon: Ban, sev: "high", tint: "#FF5C7A" },
];

const ReportScreen = ({ onDone }) => {
  const [type, setType] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const useLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not available");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const la = p.coords.latitude.toFixed(4), ln = p.coords.longitude.toFixed(4);
        setLocation(`My location (${la}, ${ln})`);
        toast.success("Location attached");
      },
      () => toast.error("Turn on GPS to attach your location"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const submit = async () => {
    const t = TYPES.find((x) => x.key === type);
    const loc = location.trim() || "Barbados (location not specified)";
    setSending(true);
    try {
      await reportIncident({ type, description: note.trim() || type, location: loc, severity: t?.sev || "low", reporter_name: "Citizen (app)" });
      setSent(true);
      toast.success("Report sent to the Command Centre");
      setTimeout(() => { setSent(false); setType(null); setNote(""); setLocation(""); onDone && onDone(); }, 1600);
    } catch {
      toast.error("Couldn't send — please try again");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-8 text-center">
        <div className="go-fade-up flex h-20 w-20 items-center justify-center rounded-full bg-[#22E0A1]/15">
          <CheckCircle2 size={44} color="#22E0A1" strokeWidth={2.4} />
        </div>
        <h2 className="font-head mt-5 text-2xl font-extrabold text-white">Thank you!</h2>
        <p className="mt-2 text-sm text-[#8194B2]">Your report is now live on the National Command Centre dashboard.</p>
      </div>
    );
  }

  return (
    <div className="go-scroll min-h-full overflow-y-auto px-5 pb-40 pt-[max(env(safe-area-inset-top),16px)]">
      <header className="go-fade-up mb-5">
        <h1 className="font-head text-2xl font-extrabold text-white">Report an incident</h1>
        <p className="mt-1 text-sm text-[#8194B2]">Help fellow Bajans — your report reaches command instantly.</p>
      </header>

      <h3 className="font-head mb-3 text-sm font-bold uppercase tracking-wide text-[#67799A]">What's happening?</h3>
      <div className="go-grid2">
        {TYPES.map(({ key, Icon, tint }) => {
          const on = type === key;
          return (
            <button key={key} onClick={() => setType(key)}
              className={`flex flex-col items-start gap-3 rounded-3xl border-2 p-5 transition-all active:scale-95 ${on ? "border-[#5B9BFF] bg-[#1E4DB7]/20" : "go-card border-transparent"}`}
              data-testid={`report-type-${key.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `${tint}22` }}>
                <Icon size={24} color={tint} strokeWidth={2.3} />
              </div>
              <span className="font-head text-sm font-bold text-white">{key}</span>
            </button>
          );
        })}
      </div>

      {type && (
        <div className="go-fade-up mt-6 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#A9B8D2]">Location</label>
            <div className="flex gap-2">
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. ABC Highway near Warrens"
                className="go-card w-full rounded-2xl px-4 py-3 text-[15px] font-medium text-white outline-none placeholder:text-[#5f7193] focus:border-[#5B9BFF]"
                data-testid="report-location" />
              <button onClick={useLocation} className="flex shrink-0 items-center justify-center rounded-2xl bg-[#1E4DB7] px-4 text-white active:scale-95" data-testid="report-use-location">
                <MapPin size={20} strokeWidth={2.4} />
              </button>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-[#A9B8D2]">Add a note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Anything drivers should know?"
              className="go-card w-full resize-none rounded-2xl px-4 py-3 text-[15px] font-medium text-white outline-none placeholder:text-[#5f7193] focus:border-[#5B9BFF]"
              data-testid="report-note" />
          </div>
          <button onClick={submit} disabled={sending}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF5C7A] py-4 text-base font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
            data-testid="report-submit">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} strokeWidth={2.5} />}
            {sending ? "Sending…" : "Send report"}
          </button>
          <p className="text-center text-xs text-[#67799A]">Reports are anonymous.</p>
        </div>
      )}
    </div>
  );
};

export default ReportScreen;
